import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { EVENT_DISPLAY_NAME } from "@/features/notification-settings/utils/eventLabels";
import { formatInvoicePeriodDate } from "@/pages/Dashboard/CustomerSubscriptionOverview/components/subscriptionInvoiceUtils";
import type {
  GetCustomerSubscriptionNotifications,
  NotificationEvent,
} from "@/server-types";

export type SubscriptionNotificationLog =
  GetCustomerSubscriptionNotifications["response"]["notifications"][number];

export type SubscriptionNotificationRow = {
  key: string;
  label: string;
  period: string | null;
  channel: SubscriptionNotificationLog["originalType"];
  isFailed: boolean;
  log: SubscriptionNotificationLog;
};

function isNotificationEvent(event: string | null): event is NotificationEvent {
  return event != null && event in EVENT_DISPLAY_NAME;
}

function notificationLabel(log: SubscriptionNotificationLog): string {
  if (isNotificationEvent(log.event)) {
    return i18n._(EVENT_DISPLAY_NAME[log.event]);
  }
  return log.settingName || t`Neznano obvestilo`;
}

function periodLabel(log: SubscriptionNotificationLog): string | null {
  if (log.periodStart == null || log.periodEnd == null) return null;
  return `${formatInvoicePeriodDate(log.periodStart)} – ${formatInvoicePeriodDate(log.periodEnd)}`;
}

export function isFailedSubscriptionLog(
  log: Pick<SubscriptionNotificationLog, "status" | "deliveryStatus">,
): boolean {
  return (
    log.deliveryStatus === "error" ||
    log.status === "error" ||
    log.status === "failed" ||
    log.status === "blocked"
  );
}

export function buildSubscriptionNotificationRows(
  logs: SubscriptionNotificationLog[],
): SubscriptionNotificationRow[] {
  return logs.map((log) => ({
    key: `log-${log.notificationLogId}`,
    label: notificationLabel(log),
    period: periodLabel(log),
    // An SMS row keeps no actual channel until its delivery report arrives.
    channel: log.notificationType ?? log.originalType,
    isFailed: isFailedSubscriptionLog(log),
    log,
  }));
}
